import { ButtonGroup, Flex, Tooltip, useDisclosure } from "@chakra-ui/react";
import React from "react";
import { LuArchive, LuArchiveRestore } from "react-icons/lu";

import { ConfirmModal, DuplicateIcon, IconButton } from "../../../components";
import {
  AiInterviewerListItemFragment,
  AiInterviewerStatus,
} from "../../graphql";
import {
  REQUIRES_BH_SCREEN_MANAGE_PERMISSION,
  useBHScreenPermission,
} from "../../hooks/useBHScreenPermission";
import { useIsBHScreenRbacEnabled } from "../../hooks/useIsBHScreenRbacEnabled";
import { ShareAiInterviewerButton } from "../AiInterviewer/ShareAiInterviewerButton";
import useAiInterviewer from "../AiInterviewer/useAiInterviewer";

const AiInterviewerActions: React.FC<{
  interviewer: AiInterviewerListItemFragment;
}> = ({ interviewer }) => {
  const {
    updateInterviewerStatus,
    duplicateAiInterviewer,
    unarchiveAiInterviewer,
  } = useAiInterviewer({
    aiInterviewerId: interviewer.id,
  });

  const archiveModalDisclosure = useDisclosure();
  const unarchiveModalDisclosure = useDisclosure();
  const { canManage } = useBHScreenPermission(interviewer);
  const isBHScreenRbacEnabled = useIsBHScreenRbacEnabled();

  const isArchived = interviewer.status === AiInterviewerStatus.Archived;

  const handleArchive = (): void => {
    updateInterviewerStatus.mutation(AiInterviewerStatus.Archived);
    archiveModalDisclosure.onClose();
  };

  const handleUnarchive = (): void => {
    unarchiveAiInterviewer.mutation(["AiInterviewers"]);
    unarchiveModalDisclosure.onClose();
  };

  return (
    <Flex width="100%" alignItems="center" justifyContent="end">
      <ButtonGroup variant="ghost" size="md" ml="auto">
        {isBHScreenRbacEnabled && (
          <ShareAiInterviewerButton aiInterviewer={interviewer} iconOnly />
        )}
        <Tooltip
          label={
            !canManage ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION : "Duplicate"
          }
        >
          <IconButton
            aria-label="Duplicate"
            variant="ghost"
            size="sm"
            color="gray.900"
            icon={<DuplicateIcon boxSize="5" strokeWidth="1.2" />}
            isDisabled={!canManage}
            onClick={() => {
              duplicateAiInterviewer.mutation(["AiInterviewers"]);
            }}
          />
        </Tooltip>
        {isArchived ? (
          <Tooltip
            label={
              !canManage ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION : "Unarchive"
            }
          >
            <IconButton
              aria-label="Unarchive"
              variant="ghost"
              size="sm"
              color="gray.900"
              icon={<LuArchiveRestore strokeWidth="1.6" size="18px" />}
              isDisabled={unarchiveAiInterviewer.loading || !canManage}
              onClick={unarchiveModalDisclosure.onOpen}
            />
          </Tooltip>
        ) : (
          <Tooltip
            label={
              !canManage ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION : "Archive"
            }
          >
            <IconButton
              aria-label="Archive"
              variant="ghost"
              size="sm"
              color="gray.900"
              icon={<LuArchive strokeWidth="1.6" size="18px" />}
              isDisabled={updateInterviewerStatus.loading || !canManage}
              onClick={archiveModalDisclosure.onOpen}
            />
          </Tooltip>
        )}
        <ConfirmModal
          isOpen={archiveModalDisclosure.isOpen}
          onCancel={archiveModalDisclosure.onClose}
          onConfirm={handleArchive}
          modalTitleText="Archive AI Interviewer?"
          modalBodyText="Archiving will remove this AI Interviewer from active use. You can unarchive it later. Are you sure?"
          modalSubmitText="Archive"
          isDangerous
        />
        <ConfirmModal
          isOpen={unarchiveModalDisclosure.isOpen}
          onCancel={unarchiveModalDisclosure.onClose}
          onConfirm={handleUnarchive}
          modalTitleText="Unarchive AI Interviewer?"
          modalBodyText="This will restore the AI Interviewer as a draft. Are you sure?"
          modalSubmitText="Unarchive"
          isDangerous={false}
        />
      </ButtonGroup>
    </Flex>
  );
};

export { AiInterviewerActions };
